import React, {
    useState,
    useEffect,
    useCallback,
    useMemo,
    useContext
} from 'react';

import { useValidation } from 'gw-portals-validation-react';
import { ViewModelForm } from 'gw-portals-viewmodel-react';
import SgIdGenerator from 'sg-id-generator';
import { TranslatorContext } from '@jutro/locale';
import _ from 'lodash';
import { SgSessionStorageService } from 'saga-modules';
import SgSpmClaimForm from 'sg-spm-claim-form';
import SpmUtils from 'sg-spm-utils';
import SgSpmClaimsTable from './sg-spm-claims-table/SgSpmClaimsTable';

import metadata from './SgSpmClaimDetails.metadata.json5';
import messages from './SgSpmClaimDetails.messages';

function SgSpmClaimDetails(props) {
    const {
        id,
        modelVM,
        showErrors,
        path,
        updateData,
        onValidate: onValidateParent,
        messageParams,
        adobeIdSuffix
    } = props;

    const {
        isComponentValid,
        onValidate
    } = useValidation(id);

    const translator = useContext(TranslatorContext);

    const commonProps = {
        modelVM, showErrors, updateData
    };

    const [isClaimFormVisible, setIsClaimFormVisible] = useState(false);
    const [oldClaimDetails, setOldClaimDetails] = useState(false);
    const [editingClaimIndex, setEditingClaimIndex] = useState(-1);
    const [showUpdateNeeded, setShowUpdateNeeded] = useState(false);

    useEffect(() => {
        if (onValidateParent) {
            onValidateParent(isComponentValid, id);
        }
    }, [id, isComponentValid, onValidateParent]);

    useEffect(() => {
        const isRetrieval = SgSessionStorageService.getItem(SgSessionStorageService.KEY.IS_RETRIEVAL_JOURNEY);
        setShowUpdateNeeded(isRetrieval === 'true' && !!_.get(modelVM, `${path}.hasMotorClaims.value`) && !_.get(modelVM, `${path}.claims.length`));
    }, []);

    const createClaim = useCallback(() => {
        setIsClaimFormVisible(true);
        const newClaimModel = {
            tempID: `claim${SgIdGenerator.getNextId()}`,
            claimDate: {}
        };
        if (_.get(modelVM, path).claims.length) {
            _.get(modelVM, path).claims.value.push(newClaimModel);
        } else {
            _.get(modelVM, path).claims.value = [newClaimModel];
        }
        setEditingClaimIndex(_.get(modelVM, path).claims.length - 1);
        updateData(modelVM);
    }, [modelVM, path, updateData]);

    useEffect(() => {
        if (_.get(modelVM, path).hasMotorClaims.value) {
            if (_.get(modelVM, path).claims.length === 0 && _.get(modelVM, path).cueClaims.length === 0) {
                createClaim();
            }
        } else {
            setIsClaimFormVisible(false);
            setShowUpdateNeeded(false);
            setEditingClaimIndex(-1);
            _.get(modelVM, path).claims.value = [];
            updateData(modelVM);
        }
    }, [_.get(modelVM, path).hasMotorClaims.value, path]);

    const onSaveClaim = useCallback(() => {
        setEditingClaimIndex(-1);
        setIsClaimFormVisible(false);
        setOldClaimDetails(null);
        SpmUtils.trackClaimsChange(modelVM);
    }, [modelVM, updateData]);

    const onCancelClaim = useCallback(() => {
        if (oldClaimDetails) {
            _.get(modelVM, path).claims.children[editingClaimIndex].value = oldClaimDetails;
            setOldClaimDetails(null);
        } else if (_.get(modelVM, path).claims.length === 1) {
            _.get(modelVM, path).claims.value = [];
            _.get(modelVM, path).hasMotorClaims.value = false;
        } else {
            _.get(modelVM, path).claims.value.splice(editingClaimIndex, 1);
        }
        setEditingClaimIndex(-1);
        setIsClaimFormVisible(false);
        setTimeout(() => {
            updateData(modelVM);
        });
        SpmUtils.trackClaimsChange(modelVM);
    }, [modelVM, updateData, oldClaimDetails, path, editingClaimIndex]);

    const onRemoveClaim = useCallback((index) => {
        _.get(modelVM, path).claims.value.splice(index, 1);
        if (_.get(modelVM, path).claims.length === 0) {
            _.get(modelVM, path).hasMotorClaims.value = false;
        }
        updateData(modelVM);
        SpmUtils.trackClaimsChange(modelVM);
    }, [modelVM, path, updateData]);

    const onEditClaim = useCallback((index) => {
        setIsClaimFormVisible(true);
        setOldClaimDetails(_.clone(_.get(modelVM, path).claims.value[index]));
        setEditingClaimIndex(index);
    }, [modelVM, path]);

    const listIsVisible = useMemo(() => {
        const numberOfClaims = _.get(modelVM, path).claims.length + _.get(modelVM, `${path}.cueClaims.length`, 0);
        return numberOfClaims > 1 || (numberOfClaims === 1 && !isClaimFormVisible);
    }, [_.get(modelVM, path).claims.length, _.get(modelVM, `${path}.cueClaims.length`), isClaimFormVisible, path]);

    const overrides = {
        hasClaims: {
            path: `${path}.hasMotorClaims`,
            visible: !listIsVisible && !oldClaimDetails,
            label: translator(messages.HasClaimsLabel, messageParams),
            adobeID: `hasClaims${adobeIdSuffix}`,
        },
        updateNeeded: {
            visible: showUpdateNeeded
        },
        spmClaimsTable: {
            visible: listIsVisible,
            claims: _.union(_.get(modelVM, path).claims.value, _.get(modelVM, `${path}.cueClaims.value`, [])),
            onRemoveClaim: onRemoveClaim,
            onEditClaim: onEditClaim,
            editingClaimIndex: editingClaimIndex,
            isClaimFormVisible
        },
        spmClaimForm: {
            visible: isClaimFormVisible,
            path: `${path}.claims.children[${editingClaimIndex}]`,
            ...commonProps,
            onCancelClaim,
            onSaveClaim,
            onValidate: onValidate,
        },
        addNewClaim: {
            visible: !isClaimFormVisible && _.get(modelVM, path).claims.length > 0
        }
    };

    const resolvers = {
        resolveCallbackMap: {
            onAddNewClaim: createClaim
        },
        resolveComponentMap: {
            SgSpmClaimForm: SgSpmClaimForm,
            SgSpmClaimsTable: SgSpmClaimsTable
        },
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            model={modelVM}
            showErrors={showErrors}
            onModelChange={updateData}
            onValidationChange={onValidate}
            overrideProps={overrides}
            callbackMap={resolvers.resolveCallbackMap}
            componentMap={resolvers.resolveComponentMap}
        />
    );
}

export default SgSpmClaimDetails;
